import type { SerializedNodes, SerializedNode, ConversionContext } from './types';
import { getAllChildElements, getAttribute } from './parser';
import {
  transformPage,
  transformContainer,
  transformSectionAsRoot,
  isValidContainerAttribute,
  transformButton,
  isValidButtonAttribute,
  transformImage,
  isValidImageAttribute,
  transformIcon,
  isValidIconAttribute,
  transformVideo,
  isValidVideoAttribute,
  transformText,
  isValidTextAttribute,
  transformCountdown,
  isValidCountdownAttribute,
  transformForm,
  isValidFormAttribute,
  getFormFieldValidator,
  getFormFieldTransform,
  type FormFieldType,
  type ContainerType,
} from './transforms';
import {
  validatePageAttributes,
  validateSectionRootAttributes,
  isPageRoot,
  isSectionRoot,
} from './validators';
import { FORM_EMAIL_FIELD_DEFAULTS } from './utils/defaults';
import { FORM_EMAIL_FIELD_NODE_NAME } from '../utils/constants';

/**
 * Result from page conversion
 */
export type PageConversionResult = {
  type: 'page';
  nodes: SerializedNodes;
};

/**
 * Result from section conversion
 */
export type SectionConversionResult = {
  type: 'section';
  rootNodeId: string;
  nodes: Record<string, SerializedNode>;
};

/**
 * Combined result type from conversion
 */
export type ConversionOutput = PageConversionResult | SectionConversionResult;

/**
 * Convert a parsed XML element tree to CraftJS nodes
 *
 * This is the main orchestrator that:
 * 1. Processes the root element (<page> or <section>)
 * 2. Recursively converts child elements
 * 3. Builds the flat node dictionary with proper parent-child links
 *
 * @param root - Root XML element (<page> or <section>)
 * @param context - Conversion context with ID generator and error collection
 * @returns ConversionOutput with type indicator and appropriate structure
 */
export const convertToSerializedNodes = (
  root: Element,
  context: ConversionContext,
): ConversionOutput => {
  if (isPageRoot(root)) {
    return convertPageRoot(root, context);
  }
  if (isSectionRoot(root)) {
    return convertSectionRoot(root, context);
  }

  // Should not happen if validation passed, but handle gracefully
  context.errors.push({
    severity: 'error',
    type: 'structural',
    message: `Unexpected root element: <${root.tagName}>`,
    element: root.tagName,
  });
  return { type: 'page', nodes: { ROOT: createEmptyRoot() } };
};

/**
 * Convert a <page> root element to PageConversionResult
 */
const convertPageRoot = (root: Element, context: ConversionContext): PageConversionResult => {
  const nodes: Record<string, SerializedNode> = {};

  // Validate page attributes (warnings)
  const attrErrors = validatePageAttributes(root);
  context.errors.push(...attrErrors);

  // Process children first to get their IDs
  const children = getAllChildElements(root);
  const childResults = children.map((child) =>
    convertElement(child, { ...context, parentId: 'ROOT' }),
  );

  // Collect child IDs and nodes
  const childIds: string[] = [];
  childResults.forEach((result) => {
    if (result) {
      childIds.push(result.id);
      nodes[result.id] = result.node;
      // Add any nested children
      result.children.forEach((childNode, childId) => {
        nodes[childId] = childNode;
      });
    }
  });

  // Transform the page element
  const pageNode = transformPage(root, context, childIds);

  return {
    type: 'page',
    nodes: {
      ROOT: pageNode,
      ...nodes,
    },
  };
};

/**
 * Convert a <section> root element to SectionConversionResult
 *
 * For section format, we generate a unique ID for the root section node
 * rather than using "ROOT" as in page format.
 */
const convertSectionRoot = (root: Element, context: ConversionContext): SectionConversionResult => {
  const nodes: Record<string, SerializedNode> = {};

  // Generate a unique ID for the section root
  const rootNodeId = context.idGenerator.generate('section');

  // Validate section attributes (warnings)
  const attrErrors = validateSectionRootAttributes(root);
  context.errors.push(...attrErrors);

  // Determine section's flex direction for passing to children
  const currentFlexDirection = getContainerFlexDirection(root, 'section');

  // Process children to get their IDs - children have rootNodeId as parent
  const children = getAllChildElements(root);
  const childResults = children.map((child) =>
    convertElement(child, {
      ...context,
      parentId: rootNodeId,
      parentFlexDirection: currentFlexDirection,
    }),
  );

  // Collect child IDs and nodes
  const childIds: string[] = [];
  childResults.forEach((result) => {
    if (result) {
      childIds.push(result.id);
      nodes[result.id] = result.node;
      // Add any nested children
      result.children.forEach((childNode, childId) => {
        nodes[childId] = childNode;
      });
    }
  });

  // Transform the section element as root
  const sectionNode = transformSectionAsRoot(root, context, childIds);

  // Add the section root node to the nodes dict
  nodes[rootNodeId] = sectionNode;

  return {
    type: 'section',
    rootNodeId,
    nodes,
  };
};

/**
 * Create an empty ROOT node for error cases
 */
const createEmptyRoot = (): SerializedNode => ({
  type: { resolvedName: 'Container' },
  isCanvas: true,
  props: {},
  displayName: 'Container',
  custom: { displayName: 'Section', isLocked: false },
  hidden: false,
  nodes: [],
  linkedNodes: {},
  parent: null,
});

/**
 * Convert a single XML element to a CraftJS node
 *
 * @param element - XML element to convert
 * @param context - Conversion context
 * @returns Object with node ID, serialized node, and any nested children
 */
export const convertElement = (
  element: Element,
  context: ConversionContext,
): { id: string; node: SerializedNode; children: Map<string, SerializedNode> } | null => {
  const tagName = element.tagName.toLowerCase();

  switch (tagName) {
    case 'section':
      return convertContainerElement(element, context, 'section');

    case 'row':
      return convertContainerElement(element, context, 'row');

    case 'column':
      return convertContainerElement(element, context, 'column');

    case 'button':
      return convertLeafElement(element, context, 'button');

    case 'image':
      return convertLeafElement(element, context, 'image');

    case 'video':
      return convertLeafElement(element, context, 'video');

    case 'text':
      return convertLeafElement(element, context, 'text');

    case 'countdown':
      return convertLeafElement(element, context, 'countdown');

    case 'icon':
      return convertLeafElement(element, context, 'icon');

    case 'form':
      return convertFormElement(element, context);

    // Form field elements
    case 'shorttext':
      return convertFormFieldElement(element, context, 'shortText');

    case 'email':
      return convertFormFieldElement(element, context, 'email');

    case 'phone':
      return convertFormFieldElement(element, context, 'phone');

    case 'dropdown':
      return convertFormFieldElement(element, context, 'dropdown');

    case 'select':
      return convertFormFieldElement(element, context, 'select');

    case 'consent':
      return convertFormFieldElement(element, context, 'consent');

    case 'submitbutton':
      return convertFormFieldElement(element, context, 'submitButton');

    // Skip <option> elements as they are handled by dropdown/select
    case 'option':
      return null;

    // Future elements will be added here:
    // case 'countdown':
    // etc.

    default:
      // Unknown element - add warning
      context.errors.push({
        severity: 'warning',
        type: 'structural',
        message: `Unknown element <${tagName}> - skipping`,
        element: tagName,
      });
      return null;
  }
};

/**
 * Determine the flex direction for a container element
 */
const getContainerFlexDirection = (element: Element, type: ContainerType): 'row' | 'column' => {
  if (type === 'row') return 'row';
  if (type === 'column') return 'column';

  // For sections, check the direction attribute
  const directionAttr = getAttribute(element, 'direction');
  if (directionAttr === 'row') return 'row';
  return 'column';
};

/**
 * Parse percentage from width string (e.g., "40%" -> 40)
 */
const parseWidthPercentage = (width: string): number | null => {
  if (!width.endsWith('%')) return null;
  const parsed = parseFloat(width);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Calculate auto-width for children without explicit width
 *
 * Two-pass approach:
 * 1. Collect explicit widths from all children
 * 2. Calculate remaining space for auto-width children
 *
 * @param childElements - Array of child elements
 * @returns Map of child index to calculated width (only for auto-width children)
 */
const calculateChildWidths = (childElements: Element[]): Map<number, string> => {
  // Pass 1: Collect explicit widths from children
  const explicitWidths: Map<number, number> = new Map();

  childElements.forEach((child, index) => {
    const widthAttr = getAttribute(child, 'width');
    if (widthAttr) {
      const parsed = parseWidthPercentage(widthAttr);
      if (parsed !== null) {
        explicitWidths.set(index, parsed);
      }
    }
  });

  // Pass 2: Calculate auto-width for children without explicit width
  const totalExplicit = Array.from(explicitWidths.values()).reduce((a, b) => a + b, 0);
  const autoCount = childElements.length - explicitWidths.size;
  // Clamp to 0 to avoid negative percentages when totalExplicit > 100
  const autoWidth = autoCount > 0 ? Math.max(0, (100 - totalExplicit) / autoCount) : 100;

  // Round to 2 decimal places
  const roundedAutoWidth = Math.round(autoWidth * 100) / 100;

  // Build result map with calculated widths for auto-width children
  const result: Map<number, string> = new Map();
  childElements.forEach((_, index) => {
    if (!explicitWidths.has(index)) {
      result.set(index, `${roundedAutoWidth}%`);
    }
  });

  return result;
};

/**
 * Convert a container element (section/row/column) with recursive child processing
 *
 * Uses two-pass approach for context-aware width calculation:
 * 1. First pass collects explicit widths from all children
 * 2. Second pass processes children with calculated widths for auto-width children
 */
const convertContainerElement = (
  element: Element,
  context: ConversionContext,
  type: ContainerType,
): { id: string; node: SerializedNode; children: Map<string, SerializedNode> } => {
  const id = context.idGenerator.generate(type);
  const allChildren = new Map<string, SerializedNode>();

  // Validate container attributes (warnings)
  const attrs = Array.from(element.attributes);
  attrs.forEach((attr) => {
    if (!isValidContainerAttribute(attr.name)) {
      context.errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <${type}>`,
        element: type,
        attribute: attr.name,
      });
    }
  });

  // Determine this container's flex direction for passing to children
  const currentFlexDirection = getContainerFlexDirection(element, type);

  // Get child elements for processing
  const childElements = getAllChildElements(element);
  const siblingCount = childElements.length;
  const childIds: string[] = [];

  // Calculate widths for children when this container is row-direction
  // Only calculate when parent direction is row (children need to share horizontal space)
  const calculatedWidths =
    currentFlexDirection === 'row'
      ? calculateChildWidths(childElements)
      : new Map<number, string>();

  // Process children with context-aware width information
  childElements.forEach((childElement, index) => {
    const calculatedWidth = calculatedWidths.get(index);

    const childResult = convertElement(childElement, {
      ...context,
      parentId: id,
      parentFlexDirection: currentFlexDirection,
      siblingCount,
      calculatedWidth,
    });

    if (childResult) {
      childIds.push(childResult.id);
      allChildren.set(childResult.id, childResult.node);
      // Add nested children from child
      childResult.children.forEach((nestedNode, nestedId) => {
        allChildren.set(nestedId, nestedNode);
      });
    }
  });

  // Transform the container element
  const node = transformContainer(
    element,
    { ...context, parentId: context.parentId },
    type,
    childIds,
  );

  return { id, node, children: allChildren };
};

/**
 * Create a default email field node with primaryEmailField: true
 */
const createDefaultPrimaryEmailField = (parentId: string): SerializedNode => ({
  type: { resolvedName: FORM_EMAIL_FIELD_NODE_NAME.replace(/\s/g, '') },
  isCanvas: false,
  props: {
    id: FORM_EMAIL_FIELD_DEFAULTS.id,
    inputType: FORM_EMAIL_FIELD_DEFAULTS.inputType,
    label: FORM_EMAIL_FIELD_DEFAULTS.label,
    showLabel: FORM_EMAIL_FIELD_DEFAULTS.showLabel,
    placeholder: FORM_EMAIL_FIELD_DEFAULTS.placeholder,
    required: FORM_EMAIL_FIELD_DEFAULTS.required,
    hidden: FORM_EMAIL_FIELD_DEFAULTS.hidden,
    helpText: FORM_EMAIL_FIELD_DEFAULTS.helpText,
    showHelpText: FORM_EMAIL_FIELD_DEFAULTS.showHelpText,
    enforceCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.enforceCharacterLimit,
    minCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.minCharacterLimit,
    maxCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.maxCharacterLimit,
    urlMapping: FORM_EMAIL_FIELD_DEFAULTS.urlMapping,
    primaryEmailField: true,
  },
  displayName: FORM_EMAIL_FIELD_NODE_NAME,
  custom: {
    displayName: 'Email',
    isLocked: false,
  },
  hidden: false,
  nodes: [],
  linkedNodes: {},
  parent: parentId,
});

/**
 * Ensure the form has a primary email field.
 * - If email fields exist, mark the first one as primary
 * - If no email fields exist, create one as the first field
 */
const ensurePrimaryEmailField = (
  childIds: string[],
  allChildren: Map<string, SerializedNode>,
  formId: string,
  context: ConversionContext,
): void => {
  const emailResolvedName = FORM_EMAIL_FIELD_NODE_NAME.replace(/\s/g, '');

  // Find first email field
  let firstEmailId: string | null = null;
  for (const childId of childIds) {
    const node = allChildren.get(childId);
    if (node?.type.resolvedName === emailResolvedName) {
      firstEmailId = childId;
      break;
    }
  }

  if (firstEmailId) {
    // Mark the first email field as primary
    const emailNode = allChildren.get(firstEmailId)!;
    emailNode.props.primaryEmailField = true;
  } else {
    // Create a default email field as the first child
    const emailId = context.idGenerator.generate('email');
    const emailNode = createDefaultPrimaryEmailField(formId);

    allChildren.set(emailId, emailNode);
    childIds.unshift(emailId);
  }
};

/**
 * Convert a <form> element with recursive child processing for form fields
 */
const convertFormElement = (
  element: Element,
  context: ConversionContext,
): { id: string; node: SerializedNode; children: Map<string, SerializedNode> } => {
  const id = context.idGenerator.generate('form');
  const allChildren = new Map<string, SerializedNode>();

  // Validate form attributes (warnings)
  const attrs = Array.from(element.attributes);
  attrs.forEach((attr) => {
    if (!isValidFormAttribute(attr.name)) {
      context.errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <form>`,
        element: 'form',
        attribute: attr.name,
      });
    }
  });

  // Process children recursively (form fields and submit button)
  const childElements = getAllChildElements(element);
  const childIds: string[] = [];

  childElements.forEach((childElement) => {
    const childResult = convertElement(childElement, { ...context, parentId: id });
    if (childResult) {
      childIds.push(childResult.id);
      allChildren.set(childResult.id, childResult.node);
      // Add nested children from child (though form fields shouldn't have nested children)
      childResult.children.forEach((nestedNode, nestedId) => {
        allChildren.set(nestedId, nestedNode);
      });
    }
  });

  // Ensure the form has a primary email field
  ensurePrimaryEmailField(childIds, allChildren, id, context);

  // Transform the form element
  const node = transformForm(element, { ...context, parentId: context.parentId }, childIds);

  return { id, node, children: allChildren };
};

/**
 * Convert a form field element (shortText/email/phone/dropdown/select/consent/submitButton)
 */
const convertFormFieldElement = (
  element: Element,
  context: ConversionContext,
  type: FormFieldType,
): { id: string; node: SerializedNode; children: Map<string, SerializedNode> } => {
  const id = context.idGenerator.generate(type);

  // Validate attributes (warnings)
  const validator = getFormFieldValidator(type);
  const attrs = Array.from(element.attributes);
  attrs.forEach((attr) => {
    if (!validator(attr.name)) {
      context.errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <${element.tagName.toLowerCase()}>`,
        element: element.tagName.toLowerCase(),
        attribute: attr.name,
      });
    }
  });

  // Transform the element
  const transform = getFormFieldTransform(type);
  const node = transform(element, { ...context, parentId: context.parentId });

  // Form field elements have no children (except dropdown/select which handle <option> internally)
  return { id, node, children: new Map() };
};

/**
 * Leaf element type for validation
 */
type LeafElementType = 'button' | 'image' | 'video' | 'text' | 'countdown' | 'icon';

/**
 * Get the validator function for a leaf element type
 */
const getLeafValidator = (type: LeafElementType): ((attr: string) => boolean) => {
  switch (type) {
    case 'button':
      return isValidButtonAttribute;
    case 'image':
      return isValidImageAttribute;
    case 'video':
      return isValidVideoAttribute;
    case 'text':
      return isValidTextAttribute;
    case 'countdown':
      return isValidCountdownAttribute;
    case 'icon':
      return isValidIconAttribute;
  }
};

/**
 * Get the transform function for a leaf element type
 */
const getLeafTransform = (
  type: LeafElementType,
): ((element: Element, context: ConversionContext) => SerializedNode) => {
  switch (type) {
    case 'button':
      return transformButton;
    case 'image':
      return transformImage;
    case 'video':
      return transformVideo;
    case 'text':
      return transformText;
    case 'countdown':
      return transformCountdown;
    case 'icon':
      return transformIcon;
  }
};

/**
 * Convert a leaf element (button/image/video) - elements without children
 */
const convertLeafElement = (
  element: Element,
  context: ConversionContext,
  type: LeafElementType,
): { id: string; node: SerializedNode; children: Map<string, SerializedNode> } => {
  const id = context.idGenerator.generate(type);

  // Validate attributes (warnings)
  const validator = getLeafValidator(type);
  const attrs = Array.from(element.attributes);
  attrs.forEach((attr) => {
    if (!validator(attr.name)) {
      context.errors.push({
        severity: 'warning',
        type: 'attribute',
        message: `Unknown attribute "${attr.name}" on <${type}>`,
        element: type,
        attribute: attr.name,
      });
    }
  });

  // Transform the element
  const transform = getLeafTransform(type);
  const node = transform(element, { ...context, parentId: context.parentId });

  // Leaf elements have no children
  return { id, node, children: new Map() };
};
